import { skipToken } from '@reduxjs/toolkit/query'
import { useEffect, useState } from 'react'
import type { ISODate } from '@sevenrooms/domain'
import { DateStatus } from '../domain'
import { useGetExperienceValidDatesQuery } from '../store'

function getFirstAvailableDateForBooking(
  data: Record<ISODate, Record<string, number>>,
  partySize: number,
  selectedDate?: string,
  includeStartDate?: boolean
) {
  const selectedDateTime = selectedDate ? new Date(selectedDate).getTime() : undefined
  return Object.entries(data)
    ?.sort((entry1, entry2) => new Date(entry1[0]).getTime() - new Date(entry2[0]).getTime())
    .find(([date, availability]) => {
      const dateTime = new Date(date).getTime()
      if (selectedDateTime && (dateTime < selectedDateTime || (!includeStartDate && dateTime <= selectedDateTime))) {
        return false
      }
      if (availability[partySize] === DateStatus.BOOKABLE) {
        return date
      }
      return false
    })?.[0]
}

interface useExperienceValidDatesProps {
  selectedDate?: string
  partySize: number
  experienceUrlKey: string
  includeStartDate?: boolean
  venueUrlKey: string
  venueToday: string
  availabilityCacheDays: number | undefined
  skipRequests?: boolean
}

export function useExperienceValidDates({
  selectedDate,
  partySize,
  experienceUrlKey,
  includeStartDate,
  venueUrlKey,
  venueToday,
  availabilityCacheDays,
  skipRequests,
}: useExperienceValidDatesProps) {
  const { currentData, isLoading, isFetching, isError } = useGetExperienceValidDatesQuery(
    !skipRequests
      ? {
          venue: venueUrlKey,
          venueToday,
          experienceUrlKey,
          availabilityCacheDays,
        }
      : skipToken
  )
  const [firstAvailableDayForBooking, setFirstAvailableDateForBooking] = useState<string | undefined>()

  useEffect(() => {
    const firstAvailableDay = currentData
      ? getFirstAvailableDateForBooking(currentData, partySize, selectedDate, includeStartDate)
      : undefined
    setFirstAvailableDateForBooking(firstAvailableDay)
  }, [currentData, includeStartDate, partySize, selectedDate])

  return {
    data: {
      availability: currentData,
      firstAvailableDayForBooking,
    },
    isLoading,
    isFetching,
    isError,
  }
}
